import { useState } from "react";
import { Navigate, NavLink, Route, Routes, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, ShoppingCart, CalendarDays, Users, Type,
  Link2, Layers, MapPin, LogOut, ArrowLeft, Mail, UserCog, Menu, X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import Overview from "./Overview";
import ProductsAdmin from "./ProductsAdmin";
import OrdersAdmin from "./OrdersAdmin";
import BookingsAdmin from "./BookingsAdmin";
import SubscribersAdmin from "./SubscribersAdmin";
import MessagesAdmin from "./MessagesAdmin";
import RolesAdmin from "./RolesAdmin";
import ContentAdmin from "./ContentAdmin";
import LinksAdmin from "./LinksAdmin";
import SectionsAdmin from "./SectionsAdmin";
import LocationsAdmin from "./LocationsAdmin";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Overview" },
  { to: "/admin/products", icon: Package, label: "Products" },
  { to: "/admin/orders", icon: ShoppingCart, label: "Orders" },
  { to: "/admin/bookings", icon: CalendarDays, label: "Bookings" },
  { to: "/admin/subscribers", icon: Users, label: "Subscribers" },
  { to: "/admin/messages", icon: Mail, label: "Messages" },
  { to: "/admin/roles", icon: UserCog, label: "Customer Dashboards" },
  { to: "/admin/content", icon: Type, label: "Content" },
  { to: "/admin/links", icon: Link2, label: "Links" },
  { to: "/admin/sections", icon: Layers, label: "Sections" },
  { to: "/admin/locations", icon: MapPin, label: "Pickup Locations" },
];

export default function AdminLayout() {
  const { user, loading, login, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) {
    return (
      <div data-testid="admin-loading" className="min-h-screen grid place-items-center bg-[#F4F1EC]">
        <div className="text-[#1F3864] font-bold animate-pulse">Loading…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-login-gate" className="min-h-screen grid place-items-center bg-[#1F3864] px-6">
        <div className="max-w-md w-full text-center">
          <img src="/rada-logo-white.png" alt="Rada Collab" className="h-8 mx-auto mb-8" />
          <h1 className="font-display font-bold text-3xl text-[#EBE6E0] mb-3">Admin Dashboard</h1>
          <p className="text-[#EBE6E0]/60 mb-8">Sign in with your Google account to manage your store.</p>
          <button
            data-testid="admin-login-btn"
            onClick={() => login()}
            className="w-full bg-[#D7E85C] text-[#1F3864] font-bold uppercase tracking-wide px-6 py-4 rounded-full hover:bg-[#EBE6E0] transition-colors"
          >
            Sign in with Google
          </button>
          <button
            onClick={() => navigate("/")}
            className="mt-4 inline-flex items-center gap-2 text-sm text-[#EBE6E0]/50 hover:text-[#EBE6E0]"
          >
            <ArrowLeft size={14} /> Back to site
          </button>
        </div>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div data-testid="admin-forbidden" className="min-h-screen grid place-items-center bg-[#1F3864] px-6">
        <div className="max-w-md w-full text-center">
          <h1 className="font-display font-bold text-3xl text-[#FF8473] mb-3">Access denied</h1>
          <p className="text-[#EBE6E0]/60 mb-8">
            {user.email} is not an admin account. Ask the store owner to add your email.
          </p>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 bg-white/10 text-[#EBE6E0] px-6 py-3 rounded-full font-bold"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </div>
    );
  }

  const sidebarNav = (onNavigate) => (
    <>
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition-colors ${
                isActive ? "bg-[#D7E85C] text-[#1F3864]" : "text-[#EBE6E0]/70 hover:bg-white/10 hover:text-[#EBE6E0]"
              }`
            }
          >
            <n.icon size={18} className="shrink-0" />
            {!collapsed && <span className="truncate">{n.label}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-2">
        <NavLink to="/" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#EBE6E0]/60 hover:bg-white/10">
          <ArrowLeft size={16} /> {!collapsed && "View site"}
        </NavLink>
        <button
          data-testid="admin-sidebar-logout"
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#FF8473] hover:bg-white/10"
        >
          <LogOut size={16} /> {!collapsed && (user.name || "Sign out")}
        </button>
      </div>
    </>
  );

  return (
    <div data-testid="admin-shell" className="min-h-screen bg-[#F4F1EC] flex">
      {/* mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#1F3864] text-[#EBE6E0] flex items-center justify-between px-4 h-14 border-b border-white/10">
        <button
          data-testid="admin-mobile-menu-toggle"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Toggle menu"
          className="grid place-items-center h-9 w-9 rounded-lg hover:bg-white/10"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <img src="/rada-logo-white.png" alt="Rada Collab" className="h-5" />
        <span className="w-9" />
      </div>

      {/* mobile drawer */}
      {mobileOpen && (
        <div
          data-testid="admin-mobile-overlay"
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside
        className={`md:hidden fixed top-14 left-0 bottom-0 z-50 w-64 bg-[#1F3864] text-[#EBE6E0] flex flex-col transform transition-transform duration-200 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {sidebarNav(() => setMobileOpen(false))}
      </aside>

      {/* desktop sidebar */}
      <aside
        className={`hidden md:flex ${collapsed ? "w-16" : "w-60"} shrink-0 bg-[#1F3864] text-[#EBE6E0] flex-col transition-[width] duration-200 sticky top-0 h-screen`}
      >
        <div className="p-4 flex items-center gap-3 border-b border-white/10">
          <img src="/rada-logo-white.png" alt="Rada Collab" className={collapsed ? "h-5" : "h-6"} />
        </div>
        {sidebarNav()}
      </aside>

      <main className="flex-1 min-w-0 p-4 pt-20 md:p-10">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="products" element={<ProductsAdmin />} />
          <Route path="orders" element={<OrdersAdmin />} />
          <Route path="bookings" element={<BookingsAdmin />} />
          <Route path="subscribers" element={<SubscribersAdmin />} />
          <Route path="messages" element={<MessagesAdmin />} />
          <Route path="roles" element={<RolesAdmin />} />
          <Route path="content" element={<ContentAdmin />} />
          <Route path="links" element={<LinksAdmin />} />
          <Route path="sections" element={<SectionsAdmin />} />
          <Route path="locations" element={<LocationsAdmin />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}
